/**
 * QuickBooks Online invoice-import CSV assembly.
 *
 * The QBO import format is strict: exact header labels, Customer/Invoice
 * Date/Due Date only on the first row of each invoice, unpadded numbers
 * ("5", not "5.00"), and M/D/YYYY dates. The generic exportToCSV helper
 * quotes every field and reformats date-like columns, so this builder is
 * hand-rolled with RFC-4180 minimal quoting instead.
 */

export interface QuickbooksInvoiceLine {
  item: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

export interface QuickbooksInvoice {
  invoiceNo: string;
  customer: string;
  invoiceDate: number;
  dueDate: number;
  salesRep: string;
  lines: QuickbooksInvoiceLine[];
}

const HEADER =
  "Invoice No,Customer,Invoice Date,Due Date,Item (Product/Service),Description,Quantity,Rate,Item Amount,Taxable,Sales Rep";

/**
 * Defuse spreadsheet formula injection: customer-controlled text (company,
 * product names) starting with =, @, or a non-numeric +/− would execute as a
 * formula if the CSV is opened in Excel/Sheets before the QBO import. A
 * leading space breaks formula interpretation without changing meaning
 * (negative amounts like "-94.08" are numeric and stay untouched).
 */
function defuseFormulaPrefix(value: string): string {
  if (/^[=@]/.test(value)) return ` ${value}`;
  if (/^[+-]/.test(value) && !/^[+-](\d|\.\d)/.test(value)) return ` ${value}`;
  return value;
}

function escapeCsvField(rawValue: string): string {
  const value = defuseFormulaPrefix(rawValue);
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/** Round to at most `decimals` places and strip trailing zeros ("5", "93.74"). */
function formatNumber(value: number, decimals: number): string {
  return String(Number(value.toFixed(decimals)));
}

export function formatQuickbooksDate(epochMs: number): string {
  const d = new Date(epochMs);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

export function buildQuickbooksCsv(invoices: QuickbooksInvoice[]): string {
  const rows: string[] = [HEADER];

  for (const invoice of invoices) {
    invoice.lines.forEach((line, index) => {
      const isFirst = index === 0;
      const cells = [
        invoice.invoiceNo,
        isFirst ? invoice.customer : "",
        isFirst ? formatQuickbooksDate(invoice.invoiceDate) : "",
        isFirst ? formatQuickbooksDate(invoice.dueDate) : "",
        line.item,
        line.description,
        formatNumber(line.quantity, 6),
        formatNumber(line.rate, 2),
        formatNumber(line.amount, 2),
        "N",
        invoice.salesRep,
      ];
      rows.push(cells.map(escapeCsvField).join(","));
    });
  }

  return `${rows.join("\n")}\n`;
}

function toDateStamp(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function quickbooksExportFilename(start: Date, end: Date): string {
  const isFullMonth =
    start.getDate() === 1 &&
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    end.getDate() === new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();

  if (isFullMonth) {
    const month = String(start.getMonth() + 1).padStart(2, "0");
    return `quickbooks-invoices-${start.getFullYear()}-${month}.csv`;
  }
  return `quickbooks-invoices-${toDateStamp(start)}_${toDateStamp(end)}.csv`;
}

export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
